import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { toast } from "sonner";
import {
  Users,
  Phone,
  Target,
  Clock,
  CheckCircle,
  XCircle,
  DollarSign,
  ArrowRight,
  Filter,
  Calendar,
  Building,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Pause,
  RefreshCw,
  Loader2,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  MapPin,
  Settings,
  Save,
  X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// RoofNuts Brand Colors
const ROOFNUTS_COLORS = {
  yellow: "#f4cc1e",
  blue: "#091c3c", 
  brown: "#9a3406",
  lightBlue: "#0d2a5a",
  cream: "#f5f2e8"
};

const KPICard = ({ title, value, subtitle, icon: Icon, color = "primary" }) => (
  <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg" data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, "-")}`}>
    <CardContent className="p-2 sm:p-3 md:p-5">
      <div className="flex items-start justify-between gap-1 sm:gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[9px] sm:text-[10px] md:text-xs uppercase tracking-wider text-white/70 font-medium truncate">{title}</p>
          <p className="text-lg sm:text-xl md:text-3xl font-bold text-white mt-0.5 sm:mt-1 font-['Chivo']">{value}</p>
          {subtitle && <p className="text-[10px] sm:text-xs md:text-sm text-white/50 mt-0.5 sm:mt-1 truncate">{subtitle}</p>}
        </div>
        <div className={`w-7 h-7 sm:w-8 sm:h-8 md:w-12 md:h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
          color === "primary" ? "bg-[#f4cc1e]/20" : 
          color === "success" ? "bg-green-500/20" :
          color === "danger" ? "bg-[#9a3406]/20" : "bg-[#1a3a6a]"
        }`}>
          <Icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-6 md:h-6 ${
            color === "primary" ? "text-[#f4cc1e]" : 
            color === "success" ? "text-green-400" :
            color === "danger" ? "text-[#9a3406]" : "text-gray-400"
          }`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const DATE_RANGES = [
  { value: "all_time", label: "All Time" },
  { value: "last_week", label: "Last Week" },
  { value: "last_30_days", label: "Last 30 Days" },
  { value: "last_6_months", label: "Last 6 Months" },
  { value: "last_12_months", label: "Last 12 Months" },
  { value: "custom", label: "Custom Range" },
];

const CustomerServiceLeadsPanel = ({ customerName }) => {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/customer-service-leads/${encodeURIComponent(customerName)}`);
      setEntries(res.data?.entries || []);
      setTotal(res.data?.total_count || 0);
    } catch (e) {
      console.error("Failed to load customer service leads:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const n = parseInt(count, 10);
    if (!n || n < 1) {
      toast.error("Enter a count of at least 1");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/customer-service-leads`, {
        customer_name: customerName,
        count: n,
        notes: notes.trim()
      });
      setCount(""); setNotes("");
      toast.success("Customer service lead added");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this entry?")) return;
    try {
      await axios.delete(`${API}/admin/customer-service-leads/${id}`);
      await load();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="pt-2 border-t border-[#1a3a6a]" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-2 mb-2">
        <CheckCircle size={14} className="text-[#f4cc1e]" />
        <span className="text-xs uppercase tracking-wider text-gray-400 font-medium">Customer Service Leads</span>
        <span className="text-xs text-[#f4cc1e] font-mono ml-auto">Total: {total}</span>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2 mb-2">
        <Input
          type="number"
          min="1"
          value={count}
          onChange={(e) => setCount(e.target.value)}
          placeholder="Count"
          className="w-full sm:w-20 h-8 text-xs bg-[#0d2a5a] border-[#1a3a6a] text-white"
        />
        <Input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes (optional)"
          className="flex-1 h-8 text-xs bg-[#0d2a5a] border-[#1a3a6a] text-white"
        />
        <Button type="submit" size="sm" disabled={submitting || !count} className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90 h-8 text-xs">
          {submitting ? "Adding..." : "Add"}
        </Button>
      </form>

      {loading ? (
        <div className="text-xs text-gray-500">Loading...</div>
      ) : entries.length === 0 ? (
        <div className="text-xs text-gray-500 italic">No customer service leads recorded yet.</div>
      ) : (
        <div className="space-y-1">
          {entries.map((e) => (
            <div key={e.id} className="flex items-center gap-2 text-xs bg-[#091c3c] rounded px-2 py-1">
              <span className="font-mono text-[#f4cc1e] w-8 flex-shrink-0">+{e.count}</span>
              <span className="text-gray-300 flex-1 truncate">{e.notes || <span className="italic text-gray-500">No note</span>}</span>
              <span className="text-gray-500 font-mono whitespace-nowrap">{new Date(e.created_at).toLocaleDateString()}</span>
              <button
                type="button"
                onClick={() => handleDelete(e.id)}
                className="text-red-400 hover:text-red-300 flex-shrink-0"
                aria-label="Delete entry"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { impersonate } = useAuth();
  
  const [totals, setTotals] = useState({
    appointments_sold: 0,
    appointments_set: 0,
    credits: 0,
    appointments_remaining: 0,
  });
  const [chartData, setChartData] = useState([]);
  const [ordersSummary, setOrdersSummary] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [callerPerformance, setCallerPerformance] = useState([]);
  const [viewMode, setViewMode] = useState("daily");
  const [loading, setLoading] = useState(true);
  
  // Data freshness tracking
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isAggregating, setIsAggregating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Filters
  const [selectedCustomer, setSelectedCustomer] = useState("all");
  const [companySearch, setCompanySearch] = useState("");
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const [dateRange, setDateRange] = useState("all_time");
  const [customDateFrom, setCustomDateFrom] = useState("");
  const [customDateTo, setCustomDateTo] = useState("");

  // Customer Orders table search, sort, and filter
  const [orderSearch, setOrderSearch] = useState("");
  const [orderSort, setOrderSort] = useState({ key: "customer_name", direction: "asc" });
  const [orderAreaFilter, setOrderAreaFilter] = useState("all");
  const [orderStatusFilter, setOrderStatusFilter] = useState("all");

  // Expandable customer row
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [customerSettings, setCustomerSettings] = useState(null);
  const [customerSettingsLoading, setCustomerSettingsLoading] = useState(false);
  const [editingSettings, setEditingSettings] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, [selectedCustomer, dateRange, customDateFrom, customDateTo, viewMode]);

  // Extract unique customers from orders summary for the dropdown
  useEffect(() => {
    if (ordersSummary.length > 0) {
      const uniqueCustomers = [...new Map(
        ordersSummary
          .filter(o => o.customer_name)
          .map(o => [o.customer_name, { id: o.id, customer_name: o.customer_name }])
      ).values()];
      uniqueCustomers.sort((a, b) => a.customer_name.localeCompare(b.customer_name));
      setCustomers(uniqueCustomers);
    }
  }, [ordersSummary]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const params = {
        date_range: dateRange,
        view_mode: viewMode,
      };
      
      if (selectedCustomer !== "all") {
        params.customer_id = selectedCustomer;
      }
      
      if (dateRange === "custom") {
        if (customDateFrom) params.date_from = customDateFrom;
        if (customDateTo) params.date_to = customDateTo;
      }

      // Use combined endpoint for faster loading (single API call instead of 4)
      const response = await axios.get(`${API}/admin/dashboard-all`, { params });
      const { totals, chart_data, orders_summary, caller_performance } = response.data;

      setTotals(totals);
      
      // Track data freshness
      if (totals.last_updated) {
        setLastUpdated(totals.last_updated);
      }
      // Only show syncing indicator when user manually triggered a refresh
      if (!refreshing) setIsAggregating(false);
      
      // Format chart data — keep full date for correct ordering
      const formattedChart = chart_data.map(d => ({
        ...d,
        date: d.date || 'N/A',
      }));
      setChartData(formattedChart);
      
      setOrdersSummary(orders_summary);
      setCallerPerformance(caller_performance);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleRefreshData = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/admin/refresh-aggregation`);
      // Poll for completion
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/admin/aggregation-status`);
          if (!statusRes.data.is_loading) {
            clearInterval(pollInterval);
            setRefreshing(false);
            fetchDashboardData(); // Refresh the dashboard
          }
        } catch (e) {
          clearInterval(pollInterval);
          setRefreshing(false);
        }
      }, 5000); // Check every 5 seconds
      
      // Timeout after 2 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        setRefreshing(false);
        fetchDashboardData();
      }, 120000);
    } catch (error) {
      console.error("Failed to trigger refresh:", error);
      setRefreshing(false);
    }
  };

  // Handle clicking on a customer name to expand/collapse
  const handleCustomerExpand = async (customerName) => {
    if (expandedCustomer === customerName) {
      setExpandedCustomer(null);
      setCustomerSettings(null);
      setEditingSettings(false);
      return;
    }

    setExpandedCustomer(customerName);
    setCustomerSettings(null);
    setEditingSettings(false);
    setCustomerSettingsLoading(true);

    try {
      const res = await axios.get(`${API}/admin/customer-settings/${encodeURIComponent(customerName)}`);
      setCustomerSettings(res.data);
    } catch (error) {
      console.error("Failed to fetch customer settings:", error);
      setCustomerSettings({ error: true });
    } finally {
      setCustomerSettingsLoading(false);
    }
  };

  const handleOpenEdit = () => {
    if (!customerSettings) return;
    setEditForm({
      zip_codes: customerSettings.zip_codes?.join(", ") || "",
      location: customerSettings.location || "",
      schedule: customerSettings.schedule ? { ...customerSettings.schedule, days: { ...customerSettings.schedule.days } } : null,
      criteria: customerSettings.criteria ? { ...customerSettings.criteria, roof_types: { ...customerSettings.criteria.roof_types } } : null,
    });
    setEditingSettings(true);
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const payload = {};
      if (editForm.zip_codes !== undefined) {
        payload.zip_codes = editForm.zip_codes.split(",").map(z => z.trim()).filter(Boolean);
      }
      if (editForm.schedule) {
        payload.schedule = editForm.schedule;
      }
      if (editForm.criteria) {
        payload.criteria = editForm.criteria;
      }
      await axios.put(`${API}/admin/customer-settings/${encodeURIComponent(expandedCustomer)}`, payload);
      toast.success("Settings saved");
      // Refresh
      const res = await axios.get(`${API}/admin/customer-settings/${encodeURIComponent(expandedCustomer)}`);
      setCustomerSettings(res.data);
      setEditingSettings(false);
    } catch (error) {
      console.error("Failed to save settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  // Calculate totals for pie chart
  const totalQualified = chartData.reduce((sum, d) => sum + (d.qualified_leads || 0), 0);
  const totalCredits = chartData.reduce((sum, d) => sum + (d.credits || 0), 0);

  const qualificationData = [
    { name: 'Qualified Leads', value: totalQualified, color: '#22C55E' },
    { name: 'Credits', value: totalCredits, color: '#EF4444' },
  ];

  const getDateRangeLabel = () => {
    const range = DATE_RANGES.find(r => r.value === dateRange);
    return range ? range.label : "Select Range";
  };

  // Customer Orders table sorting and filtering
  const handleOrderSort = (key) => {
    setOrderSort(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  const getOrderSortIcon = (key) => {
    if (orderSort.key !== key) {
      return <ArrowUpDown size={12} className="ml-1 text-muted-foreground/50" />;
    }
    return orderSort.direction === "asc" 
      ? <ArrowUp size={12} className="ml-1 text-primary" />
      : <ArrowDown size={12} className="ml-1 text-primary" />;
  };

  // Get unique areas for filter dropdown
  const uniqueAreas = useMemo(() => {
    const areas = [...new Set(ordersSummary.map(o => o.area).filter(Boolean))];
    return areas.sort();
  }, [ordersSummary]);

  // Filter and sort orders
  const filteredAndSortedOrders = useMemo(() => {
    return ordersSummary
      .filter(order => {
        // Search filter
        if (orderSearch) {
          const query = orderSearch.toLowerCase();
          const matchesSearch = 
            order.customer_name?.toLowerCase().includes(query) ||
            order.area?.toLowerCase().includes(query) ||
            order.order_date?.includes(query);
          if (!matchesSearch) return false;
        }
        // Area filter
        if (orderAreaFilter !== "all" && order.area !== orderAreaFilter) {
          return false;
        }
        // Status filter
        if (orderStatusFilter !== "all" && order.status !== orderStatusFilter) {
          return false;
        }
        return true;
      })
      .sort((a, b) => {
        const aVal = a[orderSort.key];
        const bVal = b[orderSort.key];
        
        // Handle numeric sorting for appointment columns
        if (["appointments_sold", "appointments_set", "credits", "appointments_remaining"].includes(orderSort.key)) {
          const aNum = Number(aVal) || 0;
          const bNum = Number(bVal) || 0;
          return orderSort.direction === "asc" ? aNum - bNum : bNum - aNum;
        }
        
        // String sorting
        const aStr = (aVal || "").toString().toLowerCase();
        const bStr = (bVal || "").toString().toLowerCase();
        const comparison = aStr.localeCompare(bStr);
        return orderSort.direction === "asc" ? comparison : -comparison;
      });
  }, [ordersSummary, orderSearch, orderAreaFilter, orderStatusFilter, orderSort]);

  // Count paused campaigns
  const pausedCount = useMemo(() => {
    return ordersSummary.filter(o => o.status === "paused").length;
  }, [ordersSummary]);

  // Calculate filtered totals
  const filteredTotals = useMemo(() => {
    return {
      appointments_sold: filteredAndSortedOrders.reduce((sum, o) => sum + (o.appointments_sold || 0), 0),
      appointments_set: filteredAndSortedOrders.reduce((sum, o) => sum + (o.appointments_set || 0), 0),
      credits: filteredAndSortedOrders.reduce((sum, o) => sum + (o.credits || 0), 0),
      appointments_remaining: filteredAndSortedOrders.reduce((sum, o) => sum + (o.appointments_remaining || 0), 0),
    };
  }, [filteredAndSortedOrders]);

  return (
    <div className="space-y-3 sm:space-y-6 w-full max-w-full bg-[#f4cc1e] p-4 sm:p-6 rounded-sm min-h-screen" data-testid="admin-dashboard">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl sm:text-2xl font-bold text-[#091c3c] font-['Chivo']">Dashboard Overview</h1>
        <div className="flex items-center gap-2">
          {isAggregating && (
            <span className="flex items-center gap-1 text-xs text-[#091c3c]">
              <Loader2 size={12} className="animate-spin" />
              Syncing...
            </span>
          )}
          {lastUpdated && !isAggregating && (
            <span className="text-xs text-[#091c3c]/70">
              Updated: {new Date(lastUpdated.endsWith('Z') || lastUpdated.includes('+') ? lastUpdated : lastUpdated + 'Z').toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 bg-[#091c3c] text-[#f4cc1e] border-[#091c3c] hover:bg-[#0d2a5a]"
            onClick={handleRefreshData}
            disabled={refreshing || isAggregating}
          >
            {refreshing ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <RefreshCw size={14} />
            )}
          </Button>
        </div>
      </div>

      {/* Filters Bar */}
      <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-[#f4cc1e]" />
              <span className="text-sm font-medium text-[#f4cc1e]">Filters:</span>
            </div>
            
            {/* Company Filter - Searchable */}
            <div className="flex items-center gap-2 w-full sm:w-auto relative">
              <Building size={16} className="text-gray-400 hidden sm:block" />
              <div className="relative flex-1 sm:w-56">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder={selectedCustomer === "all" ? "Search companies..." : customers.find(c => c.id === selectedCustomer)?.customer_name || "Search..."}
                  value={companySearch}
                  onChange={(e) => { setCompanySearch(e.target.value); setCompanyDropdownOpen(true); }}
                  onFocus={() => setCompanyDropdownOpen(true)}
                  className="w-full h-9 pl-8 pr-8 text-sm bg-[#0d2a5a] border border-[#1a3a6a] text-white rounded-md focus:outline-none focus:ring-1 focus:ring-[#f4cc1e] placeholder:text-gray-400"
                  data-testid="customer-filter"
                />
                {(selectedCustomer !== "all" || companySearch) && (
                  <button
                    onClick={() => { setSelectedCustomer("all"); setCompanySearch(""); setCompanyDropdownOpen(false); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    <X size={14} />
                  </button>
                )}
                {companyDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setCompanyDropdownOpen(false)} />
                    <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-[#091c3c] border border-[#1a3a6a] rounded-md shadow-xl max-h-64 overflow-y-auto">
                      <div
                        className="px-3 py-2 text-sm text-gray-300 hover:bg-[#0d2a5a] cursor-pointer"
                        onClick={() => { setSelectedCustomer("all"); setCompanySearch(""); setCompanyDropdownOpen(false); }}
                      >
                        All Companies
                      </div>
                      {customers
                        .filter(c => c.customer_name.toLowerCase().includes(companySearch.toLowerCase()))
                        .map((c) => (
                          <div
                            key={c.id}
                            className={`px-3 py-2 text-sm cursor-pointer ${selectedCustomer === c.id ? "bg-[#f4cc1e]/20 text-[#f4cc1e]" : "text-gray-300 hover:bg-[#0d2a5a]"}`}
                            onClick={() => { setSelectedCustomer(c.id); setCompanySearch(""); setCompanyDropdownOpen(false); }}
                          >
                            {c.customer_name}
                          </div>
                        ))
                      }
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Date Range Filter */}
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Calendar size={16} className="text-gray-400 hidden sm:block" />
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="flex-1 sm:w-40 h-9 text-sm bg-[#0d2a5a] border-[#1a3a6a] text-white" data-testid="date-range-filter">
                  <SelectValue placeholder="Date Range" />
                </SelectTrigger>
                <SelectContent className="bg-[#091c3c] border-[#1a3a6a]">
                  {DATE_RANGES.map((range) => (
                    <SelectItem key={range.value} value={range.value}>
                      {range.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Custom Date Range */}
            {dateRange === "custom" && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9">
                    {customDateFrom && customDateTo 
                      ? `${customDateFrom} - ${customDateTo}`
                      : "Set Dates"
                    }
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-4" align="start">
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label className="text-xs">From</Label>
                      <Input
                        type="date"
                        value={customDateFrom}
                        onChange={(e) => setCustomDateFrom(e.target.value)}
                        className="h-8"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">To</Label>
                      <Input
                        type="date"
                        value={customDateTo}
                        onChange={(e) => setCustomDateTo(e.target.value)}
                        className="h-8 bg-[#091c3c] border-[#1a3a6a] text-white"
                      />
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            )}

            {/* Clear Filters */}
            {(selectedCustomer !== "all" || dateRange !== "all_time") && (
              <Button
                variant="ghost"
                size="sm"
                className="h-9 text-gray-300 hover:text-white hover:bg-[#1a3a6a]"
                onClick={() => {
                  setSelectedCustomer("all");
                  setDateRange("all_time");
                  setCustomDateFrom("");
                  setCustomDateTo("");
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#f4cc1e]"></div>
        </div>
      ) : (
        <>
          {/* Top KPI Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
            <KPICard
              title="Appts Set"
              value={totals.appointments_set}
              icon={CheckCircle}
              color="success"
            />
            <KPICard
              title="Credits"
              value={totals.credits}
              icon={XCircle}
              color="danger"
            />
            <KPICard
              title="Remaining"
              value={totals.appointments_remaining}
              icon={Clock}
              color="primary"
            />
            <KPICard
              title="Sold"
              value={totals.appointments_sold}
              icon={DollarSign}
              color="secondary"
            />
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Main Trend Chart */}
            <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl lg:col-span-2 shadow-lg">
              <CardHeader className="border-b border-[#1a3a6a] pb-3 sm:pb-4 px-3 sm:px-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <CardTitle className="font-['Chivo'] text-base sm:text-lg text-white">Qualified Leads vs Credits</CardTitle>
                  <Select value={viewMode} onValueChange={setViewMode}>
                    <SelectTrigger className="w-28 sm:w-32 h-8 text-sm bg-[#0d2a5a] border-[#1a3a6a] text-white" data-testid="view-mode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#091c3c] border-[#1a3a6a]">
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent className="p-2 sm:p-4">
                <div className="h-48 sm:h-72">
                  {chartData.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                      No data for selected filters
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={chartData} margin={{ top: 10, right: 5, left: -15, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1a3a6a" />
                        <XAxis 
                          dataKey="date"
                          stroke="#6b7280"
                          fontSize={10}
                          tick={{ fontSize: 9, fill: '#9ca3af' }}
                        />
                        <YAxis stroke="#6b7280" fontSize={10} width={30} tick={{ fill: '#9ca3af' }} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#091c3c',
                            border: '1px solid #1a3a6a',
                            borderRadius: '4px',
                            color: '#ffffff',
                          }}
                          itemStyle={{
                            color: '#ffffff',
                          }}
                          labelStyle={{
                            color: '#f4cc1e',
                          }}
                        />
                        <Legend wrapperStyle={{ fontSize: '11px' }} />
                        <Bar dataKey="qualified_leads" name="Qualified Leads" fill="#f4cc1e" radius={[2, 2, 0, 0]} />
                        <Bar dataKey="credits" name="Credits" fill="#9a3406" radius={[2, 2, 0, 0]} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Set Rate */}
            <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
              <CardHeader className="border-b border-[#1a3a6a] pb-3 sm:pb-4 px-3 sm:px-6">
                <CardTitle className="font-['Chivo'] text-base sm:text-lg text-white">Set Rate</CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6">
                {(() => {
                  const inspected = totals.appointments_inspected || 0;
                  const set = totals.appointments_set || 0;
                  const rate = set > 0 ? (inspected / set) * 100 : 0;
                  return (
                    <div className="flex flex-col items-center justify-center h-40 sm:h-52">
                      <p className="text-5xl sm:text-7xl font-bold font-['Chivo'] text-[#f4cc1e]">
                        {set > 0 ? `${rate.toFixed(1)}%` : "—"}
                      </p>
                      <p className="text-xs text-white/50 mt-3 uppercase tracking-wider">
                        Inspected / Appts Set
                      </p>
                      <div className="flex items-center gap-6 mt-4 text-sm">
                        <div className="flex flex-col items-center">
                          <span className="text-white font-mono font-semibold">{inspected.toLocaleString()}</span>
                          <span className="text-xs text-white/50">Inspected</span>
                        </div>
                        <span className="text-white/30">/</span>
                        <div className="flex flex-col items-center">
                          <span className="text-white font-mono font-semibold">{set.toLocaleString()}</span>
                          <span className="text-xs text-white/50">Appts Set</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </div>

          {/* Orders Summary Table */}
          <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
            <CardHeader className="border-b border-[#1a3a6a] pb-3 sm:pb-4 px-3 sm:px-6">
              <div className="flex flex-col gap-3">
                <CardTitle className="font-['Chivo'] text-base sm:text-lg text-white">Customer Orders</CardTitle>
                
                {/* Search and Filter Row */}
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <div className="relative flex-1">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      placeholder="Search customers..."
                      value={orderSearch}
                      onChange={(e) => setOrderSearch(e.target.value)}
                      className="pl-8 h-8 text-sm bg-[#0d2a5a] border-[#1a3a6a] text-white placeholder:text-gray-500"
                    />
                  </div>
                  <Select value={orderAreaFilter} onValueChange={setOrderAreaFilter}>
                    <SelectTrigger className="w-full sm:w-36 h-8 text-sm bg-[#0d2a5a] border-[#1a3a6a] text-white">
                      <SelectValue placeholder="All Areas" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#091c3c] border-[#1a3a6a]">
                      <SelectItem value="all">All Areas</SelectItem>
                      {uniqueAreas.map(area => (
                        <SelectItem key={area} value={area}>{area}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={orderStatusFilter} onValueChange={setOrderStatusFilter}>
                    <SelectTrigger className="w-full sm:w-36 h-8 text-sm bg-[#0d2a5a] border-[#1a3a6a] text-white">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#091c3c] border-[#1a3a6a]">
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="paused">Paused ({pausedCount})</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Results count */}
                <div className="text-xs text-gray-400">
                  Showing {filteredAndSortedOrders.length} of {ordersSummary.length} orders
                  {orderSearch && <span> matching "{orderSearch}"</span>}
                  {pausedCount > 0 && <span className="ml-2 text-[#f4cc1e]">({pausedCount} paused)</span>}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead>
                    <tr className="border-b border-[#1a3a6a]">
                      <th 
                        className="bg-[#091c3c] text-[#f4cc1e] uppercase text-[10px] sm:text-xs font-bold px-2 sm:px-4 py-2 sm:py-3 text-left cursor-pointer hover:bg-[#0d2a5a] transition-colors"
                        onClick={() => handleOrderSort("customer_name")}
                      >
                        <div className="flex items-center">
                          Customer {getOrderSortIcon("customer_name")}
                        </div>
                      </th>
                      <th 
                        className="bg-[#091c3c] text-[#f4cc1e] uppercase text-[10px] sm:text-xs font-bold px-2 sm:px-4 py-2 sm:py-3 text-left hidden sm:table-cell cursor-pointer hover:bg-[#0d2a5a] transition-colors"
                        onClick={() => handleOrderSort("area")}
                      >
                        <div className="flex items-center">
                          Area {getOrderSortIcon("area")}
                        </div>
                      </th>
                      <th 
                        className="bg-[#091c3c] text-[#f4cc1e] uppercase text-[10px] sm:text-xs font-bold px-2 sm:px-4 py-2 sm:py-3 text-left hidden md:table-cell cursor-pointer hover:bg-[#0d2a5a] transition-colors"
                        onClick={() => handleOrderSort("order_date")}
                      >
                        <div className="flex items-center">
                          Date {getOrderSortIcon("order_date")}
                        </div>
                      </th>
                      <th 
                        className="bg-[#091c3c] text-[#f4cc1e] uppercase text-[10px] sm:text-xs font-bold px-2 sm:px-4 py-2 sm:py-3 text-right cursor-pointer hover:bg-[#0d2a5a] transition-colors"
                        onClick={() => handleOrderSort("appointments_sold")}
                      >
                        <div className="flex items-center justify-end">
                          Sold {getOrderSortIcon("appointments_sold")}
                        </div>
                      </th>
                      <th 
                        className="bg-[#091c3c] text-[#f4cc1e] uppercase text-[10px] sm:text-xs font-bold px-2 sm:px-4 py-2 sm:py-3 text-right cursor-pointer hover:bg-[#0d2a5a] transition-colors"
                        onClick={() => handleOrderSort("appointments_set")}
                      >
                        <div className="flex items-center justify-end">
                          Set {getOrderSortIcon("appointments_set")}
                        </div>
                      </th>
                      <th 
                        className="bg-[#091c3c] text-[#f4cc1e] uppercase text-[10px] sm:text-xs font-bold px-2 sm:px-4 py-2 sm:py-3 text-right cursor-pointer hover:bg-[#0d2a5a] transition-colors"
                        onClick={() => handleOrderSort("credits")}
                      >
                        <div className="flex items-center justify-end">
                          Credits {getOrderSortIcon("credits")}
                        </div>
                      </th>
                      <th
                        className="bg-[#091c3c] text-[#f4cc1e] uppercase text-[10px] sm:text-xs font-bold px-2 sm:px-4 py-2 sm:py-3 text-right cursor-pointer hover:bg-[#0d2a5a] transition-colors"
                        onClick={() => handleOrderSort("appointments_remaining")}
                      >
                        <div className="flex items-center justify-end">
                          Left {getOrderSortIcon("appointments_remaining")}
                        </div>
                      </th>
                      <th
                        className="bg-[#091c3c] text-[#f4cc1e] uppercase text-[10px] sm:text-xs font-bold px-2 sm:px-4 py-2 sm:py-3 text-center cursor-pointer hover:bg-[#0d2a5a] transition-colors"
                        onClick={() => handleOrderSort("health")}
                      >
                        <div className="flex items-center justify-center">
                          Health {getOrderSortIcon("health")}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAndSortedOrders.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center py-8 text-gray-400 text-sm">
                          No orders found
                        </td>
                      </tr>
                    ) : (
                      <>
                        {filteredAndSortedOrders.map((order) => (
                          <React.Fragment key={order.id}>
                          <tr className={`border-b border-[#1a3a6a] hover:bg-[#0d2a5a] cursor-pointer ${order.status === "paused" ? "bg-[#f4cc1e]/10" : ""} ${expandedCustomer === order.customer_name ? "bg-[#0d2a5a]" : ""}`}
                              onClick={() => handleCustomerExpand(order.customer_name)}>
                            <td className="px-2 sm:px-4 py-2 sm:py-3 font-medium text-xs sm:text-sm">
                              <div className="flex items-center gap-2">
                                <span className="text-gray-500 flex-shrink-0">
                                  {expandedCustomer === order.customer_name ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                </span>
                                <span className="text-white hover:text-[#f4cc1e] transition-colors text-left">
                                  {order.customer_name}
                                </span>
                                {order.status === "paused" && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#f4cc1e]/20 text-[#f4cc1e]">
                                    <Pause size={10} />
                                    Paused
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-2 sm:px-4 py-2 sm:py-3 text-gray-400 text-xs sm:text-sm hidden sm:table-cell">{order.area}</td>
                            <td className="px-2 sm:px-4 py-2 sm:py-3 text-gray-400 font-mono text-xs hidden md:table-cell">{order.order_date}</td>
                            <td className="px-2 sm:px-4 py-2 sm:py-3 text-right font-mono text-xs sm:text-sm text-white">{order.appointments_sold}</td>
                            <td className="px-2 sm:px-4 py-2 sm:py-3 text-right font-mono text-xs sm:text-sm text-green-400">{order.appointments_set}</td>
                            <td className="px-2 sm:px-4 py-2 sm:py-3 text-right font-mono text-xs sm:text-sm text-orange-400">{order.credits}</td>
                            <td className="px-2 sm:px-4 py-2 sm:py-3 text-right font-mono font-bold text-xs sm:text-sm text-[#f4cc1e]">{order.appointments_remaining}</td>
                            <td className="px-2 sm:px-4 py-2 sm:py-3 text-center text-xs sm:text-sm">
                              {order.health ? (
                                <span className={`inline-flex px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium ${
                                  /good|great|excellent|healthy/i.test(order.health) ? "bg-green-500/20 text-green-400" :
                                  /fair|ok|average|moderate/i.test(order.health) ? "bg-yellow-500/20 text-yellow-400" :
                                  /poor|bad|critical|at.?risk/i.test(order.health) ? "bg-red-500/20 text-red-400" :
                                  "bg-gray-500/20 text-gray-400"
                                }`}>
                                  {order.health}
                                </span>
                              ) : (
                                <span className="text-gray-600">—</span>
                              )}
                            </td>
                          </tr>
                          {expandedCustomer === order.customer_name && (
                            <tr className="bg-[#0a2040] border-b border-[#1a3a6a]">
                              <td colSpan={8} className="px-4 py-4">
                                {customerSettingsLoading ? (
                                  <div className="flex items-center gap-2 text-gray-400 text-sm">
                                    <Loader2 size={14} className="animate-spin" /> Loading settings...
                                  </div>
                                ) : customerSettings?.error ? (
                                  <div className="text-red-400 text-sm">Failed to load settings</div>
                                ) : !editingSettings ? (
                                  <div className="space-y-3">
                                    {/* Zip Codes Display */}
                                    <div>
                                      <div className="flex items-center gap-2 mb-2">
                                        <MapPin size={14} className="text-[#f4cc1e]" />
                                        <span className="text-xs uppercase tracking-wider text-gray-400 font-medium">Zip Codes</span>
                                        {customerSettings?.location && (
                                          <span className="text-xs text-gray-500">({customerSettings.location})</span>
                                        )}
                                      </div>
                                      {customerSettings?.zip_codes?.length > 0 ? (
                                        <div className="flex flex-wrap gap-1.5">
                                          {customerSettings.zip_codes.map((zip, i) => (
                                            <span key={i} className="px-2 py-0.5 rounded text-xs font-mono bg-[#1a3a6a] text-gray-300">
                                              {zip}
                                            </span>
                                          ))}
                                        </div>
                                      ) : (
                                        <span className="text-xs text-gray-500 italic">No zip codes configured</span>
                                      )}
                                    </div>

                                    {/* Quick info row */}
                                    {customerSettings?.schedule && (
                                      <div className="flex flex-wrap gap-4 text-xs text-gray-400">
                                        <span>Leads/day: <span className="text-white">{customerSettings.schedule.leads_per_day || "—"}</span></span>
                                        <span>Reps: <span className="text-white">{customerSettings.schedule.reps || "—"}</span></span>
                                        <span>Active: <span className={customerSettings.schedule.active === "YES" ? "text-green-400" : "text-red-400"}>{customerSettings.schedule.active || "—"}</span></span>
                                      </div>
                                    )}

                                    {/* Customer Service Leads Panel */}
                                    <CustomerServiceLeadsPanel customerName={order.customer_name} />

                                    {/* Manage Profile Button */}
                                    <div className="pt-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); handleOpenEdit(); }}
                                        className="text-[#f4cc1e] hover:bg-[#1a3a6a] h-7 text-xs"
                                      >
                                        <Settings size={12} className="mr-1" /> Manage Profile
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  /* Inline Edit Panel */
                                  <div className="space-y-4" onClick={(e) => e.stopPropagation()}>
                                    <div className="flex items-center justify-between">
                                      <h4 className="text-sm font-bold text-white font-['Chivo']">Edit Settings — {expandedCustomer}</h4>
                                      <div className="flex gap-2">
                                        <Button size="sm" onClick={handleSaveSettings} disabled={savingSettings}
                                          className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/80 h-7 text-xs">
                                          {savingSettings ? <Loader2 size={12} className="animate-spin mr-1" /> : <Save size={12} className="mr-1" />}
                                          Save
                                        </Button>
                                        <Button size="sm" variant="ghost" onClick={() => setEditingSettings(false)}
                                          className="text-gray-400 hover:bg-[#1a3a6a] h-7 text-xs">
                                          <X size={12} className="mr-1" /> Cancel
                                        </Button>
                                      </div>
                                    </div>

                                    {/* Zips Section */}
                                    <div className="bg-[#091c3c] rounded-lg p-3 space-y-2">
                                      <h5 className="text-xs font-bold text-[#f4cc1e] uppercase tracking-wider">Zip Codes</h5>
                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        <div>
                                          <Label className="text-[10px] text-gray-400">Zip Codes (comma-separated)</Label>
                                          <Input value={editForm.zip_codes || ""} onChange={(e) => setEditForm(f => ({ ...f, zip_codes: e.target.value }))}
                                            className="h-8 text-xs bg-[#0d2a5a] border-[#1a3a6a] text-white" />
                                        </div>
                                        <div>
                                          <Label className="text-[10px] text-gray-400">Location</Label>
                                          <Input value={editForm.location || ""} onChange={(e) => setEditForm(f => ({ ...f, location: e.target.value }))}
                                            className="h-8 text-xs bg-[#0d2a5a] border-[#1a3a6a] text-white" />
                                        </div>
                                      </div>
                                    </div>

                                    {/* Schedule Section */}
                                    {editForm.schedule && (
                                      <div className="bg-[#091c3c] rounded-lg p-3 space-y-2">
                                        <h5 className="text-xs font-bold text-[#f4cc1e] uppercase tracking-wider">Schedule</h5>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                          <div>
                                            <Label className="text-[10px] text-gray-400">Leads/Day</Label>
                                            <Input value={editForm.schedule.leads_per_day || ""} onChange={(e) => setEditForm(f => ({ ...f, schedule: { ...f.schedule, leads_per_day: e.target.value } }))}
                                              className="h-8 text-xs bg-[#0d2a5a] border-[#1a3a6a] text-white" />
                                          </div>
                                          <div>
                                            <Label className="text-[10px] text-gray-400">Appts/Hour</Label>
                                            <Input value={editForm.schedule.appts_per_hour || ""} onChange={(e) => setEditForm(f => ({ ...f, schedule: { ...f.schedule, appts_per_hour: e.target.value } }))}
                                              className="h-8 text-xs bg-[#0d2a5a] border-[#1a3a6a] text-white" />
                                          </div>
                                          <div>
                                            <Label className="text-[10px] text-gray-400">Reps</Label>
                                            <Input value={editForm.schedule.reps || ""} onChange={(e) => setEditForm(f => ({ ...f, schedule: { ...f.schedule, reps: e.target.value } }))}
                                              className="h-8 text-xs bg-[#0d2a5a] border-[#1a3a6a] text-white" />
                                          </div>
                                          <div>
                                            <Label className="text-[10px] text-gray-400">Notes</Label>
                                            <Input value={editForm.schedule.notes || ""} onChange={(e) => setEditForm(f => ({ ...f, schedule: { ...f.schedule, notes: e.target.value } }))}
                                              className="h-8 text-xs bg-[#0d2a5a] border-[#1a3a6a] text-white" />
                                          </div>
                                        </div>
                                        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                                          {["sat", "mon", "tue", "wed", "thu", "fri"].map(day => (
                                            <div key={day}>
                                              <Label className="text-[10px] text-gray-400 capitalize">{day}</Label>
                                              <Input value={editForm.schedule.days?.[day] || ""} onChange={(e) => setEditForm(f => ({ ...f, schedule: { ...f.schedule, days: { ...f.schedule.days, [day]: e.target.value } } }))}
                                                className="h-8 text-xs bg-[#0d2a5a] border-[#1a3a6a] text-white" />
                                            </div>
                                          ))}
                                        </div>
                                        <div className="grid grid-cols-3 gap-2">
                                          {["same_days", "double_book", "active"].map(field => (
                                            <div key={field}>
                                              <Label className="text-[10px] text-gray-400 capitalize">{field.replace("_", " ")}</Label>
                                              <Select value={editForm.schedule[field] || ""} onValueChange={(v) => setEditForm(f => ({ ...f, schedule: { ...f.schedule, [field]: v } }))}>
                                                <SelectTrigger className="h-8 text-xs bg-[#0d2a5a] border-[#1a3a6a] text-white">
                                                  <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent className="bg-[#091c3c] border-[#1a3a6a]">
                                                  <SelectItem value="YES">YES</SelectItem>
                                                  <SelectItem value="NO">NO</SelectItem>
                                                </SelectContent>
                                              </Select>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Criteria Section */}
                                    {editForm.criteria && (
                                      <div className="bg-[#091c3c] rounded-lg p-3 space-y-2">
                                        <h5 className="text-xs font-bold text-[#f4cc1e] uppercase tracking-wider">Criteria</h5>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                          <div>
                                            <Label className="text-[10px] text-gray-400">Roof Age</Label>
                                            <Input value={editForm.criteria.roof_age || ""} onChange={(e) => setEditForm(f => ({ ...f, criteria: { ...f.criteria, roof_age: e.target.value } }))}
                                              className="h-8 text-xs bg-[#0d2a5a] border-[#1a3a6a] text-white" />
                                          </div>
                                          {["denials", "cash_jobs", "financing", "spanish"].map(field => (
                                            <div key={field}>
                                              <Label className="text-[10px] text-gray-400 capitalize">{field.replace("_", " ")}</Label>
                                              <Select value={editForm.criteria[field] || ""} onValueChange={(v) => setEditForm(f => ({ ...f, criteria: { ...f.criteria, [field]: v } }))}>
                                                <SelectTrigger className="h-8 text-xs bg-[#0d2a5a] border-[#1a3a6a] text-white">
                                                  <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent className="bg-[#091c3c] border-[#1a3a6a]">
                                                  <SelectItem value="YES">YES</SelectItem>
                                                  <SelectItem value="NO">NO</SelectItem>
                                                </SelectContent>
                                              </Select>
                                            </div>
                                          ))}
                                        </div>
                                        <div>
                                          <Label className="text-[10px] text-gray-400">Roof Types</Label>
                                          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-1">
                                            {["shingles", "tile", "metal", "flat", "shake", "slate"].map(rt => (
                                              <div key={rt}>
                                                <Label className="text-[10px] text-gray-400 capitalize">{rt}</Label>
                                                <Select value={editForm.criteria.roof_types?.[rt] || ""} onValueChange={(v) => setEditForm(f => ({ ...f, criteria: { ...f.criteria, roof_types: { ...f.criteria.roof_types, [rt]: v } } }))}>
                                                  <SelectTrigger className="h-8 text-xs bg-[#0d2a5a] border-[#1a3a6a] text-white">
                                                    <SelectValue />
                                                  </SelectTrigger>
                                                  <SelectContent className="bg-[#091c3c] border-[#1a3a6a]">
                                                    <SelectItem value="YES">YES</SelectItem>
                                                    <SelectItem value="NO">NO</SelectItem>
                                                  </SelectContent>
                                                </Select>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                        <div>
                                          <Label className="text-[10px] text-gray-400">Notes</Label>
                                          <Input value={editForm.criteria.notes || ""} onChange={(e) => setEditForm(f => ({ ...f, criteria: { ...f.criteria, notes: e.target.value } }))}
                                            className="h-8 text-xs bg-[#0d2a5a] border-[#1a3a6a] text-white" />
                                        </div>
                                      </div>
                                    )}

                                    {!editForm.schedule && !editForm.criteria && (
                                      <div className="text-xs text-gray-500 italic">No schedule or criteria data found for this customer in the settings sheet.</div>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        ))}
                        {/* Totals Row */}
                        <tr className="bg-[#091c3c] font-bold">
                          <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-[#f4cc1e]" colSpan={1}>TOTAL</td>
                          <td className="hidden sm:table-cell"></td>
                          <td className="hidden md:table-cell"></td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 text-right font-mono text-xs sm:text-sm text-white">{filteredTotals.appointments_sold}</td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 text-right font-mono text-xs sm:text-sm text-green-400">{filteredTotals.appointments_set}</td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 text-right font-mono text-xs sm:text-sm text-orange-400">{filteredTotals.credits}</td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 text-right font-mono text-xs sm:text-sm text-[#f4cc1e]">{filteredTotals.appointments_remaining}</td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

        </>
      )}
    </div>
  );
};

export default AdminDashboard;
